import { FunctionComponent } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useAppSelector } from "@/redux/hooks";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { ROUTE_CONFIG } from "@/routes/config";

import { useIsFeatureActiveUnauthenticated } from "@/utils/useIsFeatureActiveUnauthenticated";
import { useOnboardingVariant } from "@/utils/useOnboardingVariant";

import { EOnboardingVariant } from "@/enum/onboarding-variant.enum";

import { OnboardingProfile } from "./pages/OnboardingProfile";
import { OnboardingChat } from "./pages/OnboardingChat";
import { OnboardingLists } from "./pages/OnboardingLists";
import { OnboardingTrack } from "./pages/OnboardingTrack";
import { OnboardingPrime } from "./pages/OnboardingPrime";
import { OnboardingDone } from "./pages/OnboardingDone";
import { OnboardingSubscription } from "./pages/OnboardingSubscription";
import { FeaturesCodes } from "../Users/enum/codes";

/** Where each persisted onboarding step resumes (PREC-3653). */
const STEP_ROUTES: Record<string, string> = {
	CHAT: ROUTE_CONFIG.ONBOARDING_CHAT,
	LISTS: ROUTE_CONFIG.ONBOARDING_LISTS,
	TRACK: ROUTE_CONFIG.ONBOARDING_TRACK,
	PRIME: ROUTE_CONFIG.ONBOARDING_PRIME,
	CHECKOUT: ROUTE_CONFIG.ONBOARDING_SUBSCRIPTION,
	// V2 persists DONE instead of CHECKOUT — it has no plan to pick (PREC-3719).
	DONE: ROUTE_CONFIG.ONBOARDING_DONE,
};

export const Onboarding: FunctionComponent = () => {
	const { user } = useAppSelector(authSelector);
	const { pathname } = useLocation();

	const variant = useOnboardingVariant();

	const SELF_REGISTER = useIsFeatureActiveUnauthenticated(FeaturesCodes.SELF_REGISTER);

	if (SELF_REGISTER === false) {
		return <Navigate to={ROUTE_CONFIG.LISTS} />;
	}

	if (variant === EOnboardingVariant.SLIM) {
		return <Navigate to={ROUTE_CONFIG.ONBOARDING_V3} />;
	}

	if (variant === EOnboardingVariant.NONE) {
		return <Navigate to={ROUTE_CONFIG.DASHBOARD} />;
	}

	// An interrupted onboarding resumes at the persisted step — e.g. a new
	// signup that quit before creating lists lands in the chat again.
	const resumeTo = user?.onboardingStep ? STEP_ROUTES[user.onboardingStep] : undefined;

	if (pathname === ROUTE_CONFIG.ONBOARDING && resumeTo) {
		return <Navigate to={resumeTo} />;
	}

	return (
		<Routes>
			<Route element={<OnboardingProfile />} path="/" />
			<Route element={<OnboardingChat />} path="/chat" />
			<Route element={<OnboardingLists />} path="/lists" />
			<Route element={<OnboardingTrack />} path="/track" />
			<Route element={<OnboardingPrime />} path="/prime" />
			<Route element={<OnboardingDone />} path="/done" />
			<Route element={<OnboardingSubscription />} path="/subscription" />
		</Routes>
	);
};
